import React, { useEffect, useRef, useState } from 'react';

// same easing as the rewards meter; a completed level also waits this long for the arc to fill up
const PROGRESS_TRANSITION_MS = 800;

// lets the emptied arc paint before it fills up for the new level
const LEVEL_RESTART_DELAY = 50;

const PROGRESS_GRADIENT_ID = 'free-drop-promo-tool-progress-gradient';

// the track arc (from 0,81) runs under the timer label (top edge at y 66) for its first ~27%, so the progress
// arc is the same ellipse starting where the track comes out from under the label (its butt end still hidden)
const PROGRESS_ARC_PATH = 'M 33.21,69.89 A 47,38 0 0,0 0,5';

interface PromoToolProgressProps {
  progress: number; // 0 - 100, inside the current level
  level: number; // reached levels count
}

export const PromoToolProgress: React.FC<PromoToolProgressProps> = ({ progress, level }) => {

  const [displayedProgress, setDisplayedProgress] = useState(progress);
  const [instant, setInstant] = useState(false);
  const previousRef = useRef({ level, progress });

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = { level, progress };

    // the first data (from nothing) just fills in; a completed level fills up and starts over from empty
    const levelCompleted = level > previous.level && (previous.level > 0 || previous.progress > 0);

    setInstant(false);
    if (!levelCompleted) {
      setDisplayedProgress(progress);
      return;
    }

    setDisplayedProgress(100);
    // the last level stays full
    if (progress >= 100) return;

    let restartTimeout: ReturnType<typeof setTimeout> | undefined;
    const emptyTimeout = setTimeout(() => {
      // back to empty without the transition, then fill up the new level
      setInstant(true);
      setDisplayedProgress(0);
      restartTimeout = setTimeout(() => {
        setInstant(false);
        setDisplayedProgress(progress);
      }, LEVEL_RESTART_DELAY);
    }, PROGRESS_TRANSITION_MS);

    return () => {
      clearTimeout(emptyTimeout);
      clearTimeout(restartTimeout);
    };
  }, [progress, level]);

  return (
    <svg
      className="promo-tool-svg"
      width="52"
      height="86"
      viewBox="0 0 52 86"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={PROGRESS_GRADIENT_ID} x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor="#FFB320" />
          <stop offset="100%" stopColor="#F26B22" />
        </linearGradient>
      </defs>
      <path
        d="M 0,81 A 47,38 0 0,0 0,5"
        stroke="#969696"
        strokeWidth="10"
        strokeLinecap="butt"
      />
      <path
        d={PROGRESS_ARC_PATH}
        stroke={`url(#${PROGRESS_GRADIENT_ID})`}
        strokeWidth="10"
        strokeLinecap="butt"
        pathLength="100"
        style={{
          strokeDasharray: `${displayedProgress} 100`,
          transition: instant ? 'none' : `stroke-dasharray ${PROGRESS_TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`
        }}
      />
    </svg>
  );
};
